/**
 * useFeatureSync — Real-Time Feature Governance (Fev/2026).
 *
 * ARQUITETURA DUPLA (SSE + Poll fallback):
 *  1. Server-Sent Events (canal principal) — push instantâneo (< 1s)
 *     - EventSource conectado em `/api/features/stream?token=...`
 *     - Reconexão automática exponencial (nativo do EventSource)
 *     - Se erro persistente → cai pro fallback
 *  2. Poll `/api/features/version` (fallback) — a cada 30s
 *     - Ativado apenas se SSE indisponível ou falhando
 *     - Só polla quando aba visível
 *
 * SEMPRE ativos:
 *  - BroadcastChannel entre abas — 1 conexão serve todas as abas
 *  - Trigger extra em `focus`, `visibilitychange`, `auth-change`
 *
 * Uso: chame `useFeatureSync(refreshCallback)` uma única vez no AuthContext.
 */
import { useEffect, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000;
const CHANNEL_NAME = 'fastconformity-feature-sync';
const STORAGE_KEY = 'fastconformity_feature_version';
const SSE_MAX_ERRORS = 3; // após 3 erros consecutivos, cai pro poll

/**
 * @param {() => void} onVersionChange - callback chamado quando versão remota != local
 * @param {() => boolean} isEnabled - opcional: retorna false pra pausar
 */
export function useFeatureSync(onVersionChange, isEnabled = () => true) {
  const pollTimerRef = useRef(null);
  const channelRef = useRef(null);
  const eventSourceRef = useRef(null);
  const lastVersionRef = useRef(null);
  const inFlightRef = useRef(false);
  const sseErrorCountRef = useRef(0);
  const sseActiveRef = useRef(false);
  const onChangeRef = useRef(onVersionChange);
  const isEnabledRef = useRef(isEnabled);

  useEffect(() => { onChangeRef.current = onVersionChange; }, [onVersionChange]);
  useEffect(() => { isEnabledRef.current = isEnabled; }, [isEnabled]);

  useEffect(() => {
    // Restaura versão do storage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) lastVersionRef.current = Number(saved);
    } catch { /* ignore */ }

    // Broadcast Channel — sincroniza entre abas abertas
    let channel = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channel.onmessage = (evt) => {
          const remoteVersion = evt?.data?.version;
          if (typeof remoteVersion === 'number' && remoteVersion !== lastVersionRef.current) {
            lastVersionRef.current = remoteVersion;
            try { localStorage.setItem(STORAGE_KEY, String(remoteVersion)); } catch { /* ignore */ }
            try { onChangeRef.current?.(); } catch { /* ignore */ }
          }
        };
        channelRef.current = channel;
      }
    } catch { /* ignore */ }

    // ═══ Processa version recebida (via SSE OU poll) ═══
    const handleVersion = (remoteVersion, isAdmin) => {
      if (isAdmin) {
        lastVersionRef.current = 0;
        return;
      }
      if (lastVersionRef.current === null) {
        // Primeira leitura → só grava
        lastVersionRef.current = remoteVersion;
        try { localStorage.setItem(STORAGE_KEY, String(remoteVersion)); } catch { /* ignore */ }
        return;
      }
      if (remoteVersion !== lastVersionRef.current) {
        lastVersionRef.current = remoteVersion;
        try { localStorage.setItem(STORAGE_KEY, String(remoteVersion)); } catch { /* ignore */ }
        try { channelRef.current?.postMessage({ version: remoteVersion }); } catch { /* ignore */ }
        try { onChangeRef.current?.(); } catch { /* ignore */ }
        try {
          window.dispatchEvent(new CustomEvent('feature-flags-changed', {
            detail: { version: remoteVersion },
          }));
        } catch { /* ignore */ }
      }
    };

    // ═══ POLL (fallback) ═══
    const checkViaPolling = async () => {
      if (inFlightRef.current) return;
      if (!isEnabledRef.current?.()) return;
      const token = localStorage.getItem('token');
      if (!token) return;

      inFlightRef.current = true;
      try {
        const res = await fetch(`${API}/api/features/version`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const data = await res.json();
        handleVersion(Number(data?.version || 0), !!data?.is_admin);
      } catch { /* silencioso — rede intermitente é OK */ }
      finally {
        inFlightRef.current = false;
      }
    };

    const startPolling = () => {
      if (pollTimerRef.current) return;
      checkViaPolling();
      pollTimerRef.current = setInterval(checkViaPolling, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    // ═══ SSE (canal principal) ═══
    const closeSSE = () => {
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch { /* ignore */ }
        eventSourceRef.current = null;
      }
      sseActiveRef.current = false;
    };

    const connectSSE = () => {
      if (typeof EventSource === 'undefined') return false;
      if (!isEnabledRef.current?.()) return false;
      const token = localStorage.getItem('token');
      if (!token) return false;
      if (eventSourceRef.current) return true; // já conectado

      try {
        const url = `${API}/api/features/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url, { withCredentials: false });
        eventSourceRef.current = es;

        es.onopen = () => {
          sseActiveRef.current = true;
          sseErrorCountRef.current = 0;
          // Para o poll — SSE cuida do resto
          stopPolling();
        };

        es.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data || '{}');
            const version = Number(data?.version ?? 0);
            handleVersion(version, !!data?.is_admin);
          } catch { /* ignore */ }
        };

        es.onerror = () => {
          sseErrorCountRef.current += 1;
          sseActiveRef.current = false;
          if (sseErrorCountRef.current >= SSE_MAX_ERRORS) {
            // Desiste do SSE → usa só poll
            closeSSE();
            startPolling();
          }
          // Caso contrário, EventSource já tenta reconectar sozinho
        };
        return true;
      } catch {
        closeSSE();
        return false;
      }
    };

    // ═══ Orquestração ═══
    const start = () => {
      if (!isEnabledRef.current?.()) return;
      const sseOk = connectSSE();
      if (!sseOk) startPolling();
    };
    const stop = () => {
      closeSSE();
      stopPolling();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        start();
        // check imediato via poll (mesmo com SSE — cobre gap durante disconnected)
        checkViaPolling();
      } else {
        stop();
      }
    };

    const handleFocus = () => checkViaPolling();

    const handleAuthChange = () => {
      lastVersionRef.current = null;
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      stop();
      if (isEnabledRef.current?.()) start();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('auth-change', handleAuthChange);

    if (document.visibilityState === 'visible') start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('auth-change', handleAuthChange);
      try { channel?.close(); } catch { /* ignore */ }
    };
  }, []); // Setup 1x — refs cuidam da atualização

  return null;
}

export default useFeatureSync;
